import { get, post, put, del } from './http';

export interface ToolCommand {
  label: string;
  cmd: string;
}

export interface ToolFaq {
  q: string;
  a: string;
}

export interface ToolDetail {
  intro: string;
  install: string[];
  usage: string[];
  faq: ToolFaq[];
}

export interface ToolAttachment {
  id?: string;
  name: string;
  size: string;
  url: string;
  downloadUrl?: string | null;
  downloadType?: 'free' | 'timed_free' | 'lingshi' | 'vip';
  downloadTypes?: ('free' | 'timed_free' | 'lingshi' | 'vip')[];
  vipPrivilege?: boolean;
  lingshiCost?: number;
  timedFreeUntil?: string;
  fileObjectId?: string | number | null;
}

export interface ToolLink {
  label: string;
  url: string;
  type: 'github' | 'docs' | 'demo' | 'video' | 'other' | 'download';
}

export interface ToolDto {
  id?: string;
  name: string;
  slug?: string;
  category: string;
  description: string;
  version?: string;
  size?: string;
  stars?: number;
  downloadCount?: number;
  downloads?: string;
  pinned?: boolean;
  offline?: boolean;
  showCommands?: boolean;
  sortOrder?: number;
  coverImage?: string;
  requirements?: string;
  longDesc?: string;
  changelog?: string;
  detailImages?: string[];
  platform?: string[];
  tags?: string[];
  commands?: ToolCommand[];
  detail?: ToolDetail;
  attachments?: ToolAttachment[];
  links?: ToolLink[];
}

export interface AdminToolsResponse {
  total: number;
  list: ToolDto[];
}

export interface PresignUploadResponse {
  uploadUrl: string;
  objectKey: string;
  bucket: string;
  endpoint: string;
}

export interface CompleteUploadResponse {
  fileObjectId: number;
  uploadedFileId: number;
  objectKey: string;
  displayName: string;
}

const PUBLIC_TOOLS_CACHE_TTL = 5000;
const publicToolsInFlight = new Map<string, Promise<{ total: number; list: ToolDto[] }>>();
const publicToolsCache = new Map<string, { cachedAt: number; data: { total: number; list: ToolDto[] } }>();

// ── 工具管理接口 ─────────────────────────────────────────────────────────────

/**
 * 1. 查询工具管理列表 (后台管理员用)
 */
export function getAdminTools(params: {
  search?: string;
  category?: string;
  page?: number;
  pageSize?: number;
}) {
  return get<AdminToolsResponse>('/admin/tools', { params });
}

/**
 * 2. 创建新工具 (普通管理员返回带审核状态数据)
 */
export function createTool(data: ToolDto, resubmitReviewId?: number | string) {
  const url = resubmitReviewId ? `/admin/tools?resubmitReviewId=${resubmitReviewId}` : '/admin/tools';
  return post<{ toolId?: number; reviewRequestId?: number; isPending?: boolean }>(
    url,
    data
  );
}

/**
 * 3. 更新工具
 */
export function updateTool(id: string | number, data: ToolDto, resubmitReviewId?: number | string) {
  const url = resubmitReviewId ? `/admin/tools/${id}?resubmitReviewId=${resubmitReviewId}` : `/admin/tools/${id}`;
  return put<{ toolId?: number; reviewRequestId?: number; isPending?: boolean }>(
    url,
    data
  );
}

/**
 * 4. 删除工具
 */
export function deleteTool(id: string | number) {
  return del<{ reviewRequestId?: number; isPending?: boolean }>(`/admin/tools/${id}`);
}

/**
 * 5. 微调单个属性 (如置顶、上下架、修改排序权重)
 */
export function updateToolDirectFields(
  id: string | number,
  fields: {
    pinned?: boolean;
    offline?: boolean;
    sortOrder?: number;
    name?: string; // 传输名称用于普通管理员显示审批文本
  }
) {
  return post<{ reviewRequestId?: number; isPending?: boolean }>(
    `/admin/tools/${id}/fields`,
    fields
  );
}

/**
 * 6. 批量操作 (批量删除/批量下架/批量上架)
 */
export function batchToolsAction(payload: {
  ids: (string | number)[];
  action: 'delete' | 'offline' | 'online';
}) {
  return post<{ reviewRequestId?: number; isPending?: boolean }>('/admin/tools/batch', payload);
}

/**
 * 7. 申请预签名直传上传 URL
 */
export function presignUploadFile(payload: {
  fileName: string;
  contentType: string;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
}) {
  return post<PresignUploadResponse>('/files/presign-upload', payload);
}

/**
 * 8. 通知后端直传完成并备案落盘
 */
export function completeUploadFile(payload: {
  objectKey: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
}) {
  return post<CompleteUploadResponse>('/files/complete', payload);
}

/**
 * 9. 公开查询工具列表 (免鉴权，前台用户与计数器使用)
 */
export function getPublicTools(params?: { search?: string; category?: string }) {
  const key = JSON.stringify(params || {});
  const now = Date.now();
  const cached = publicToolsCache.get(key);
  if (cached && now - cached.cachedAt < PUBLIC_TOOLS_CACHE_TTL) {
    return Promise.resolve(cached.data);
  }

  const inFlight = publicToolsInFlight.get(key);
  if (inFlight) return inFlight;

  const req = get<{ total: number; list: ToolDto[] }>('/public/tools', { params })
    .then((res) => {
      publicToolsCache.set(key, { cachedAt: Date.now(), data: res });
      return res;
    })
    .finally(() => {
      publicToolsInFlight.delete(key);
    });

  publicToolsInFlight.set(key, req);
  return req;
}
